import { CompatLink } from './components/Link.js';
import { CompatImage } from './components/Image.js';
import { useCompatRouter } from './hooks/useRouter.js';
import React, { forwardRef } from 'react';
import { noop } from '@fabric-app/utils/guards/noop';
import { omitUndefined } from '@fabric-app/utils/object/manipulation';
import { merge } from './node_modules/ts-deepmerge/esm/index.js';
import { SkinTones } from 'emoji-picker-react';

let wakeLockSentinel = null;
const defaultCompatConfig = {
  AUTH: {
    getRedirectUrlAfterAuth: () => null,
    setRedirectUrlAfterAuth: noop
  },
  PLATFORM: {
    getPlatform: () => "web",
    getSimplifiedPlatform: () => "web",
    getPlatformVersion: () => Promise.resolve({
      webVersion: "1.0.0"
    }),
    getWebVersion: () => "1.0.0",
    openExternal: () => false
  },
  FILE: {
    getIsAvailable: () => false,
    readFile: async () => Promise.reject(new Error("Not implemented")),
    uploadFile: async () => Promise.reject(new Error("Not implemented"))
  },
  ENVIRONMENT: {
    DASHBOARD_URL: "",
    WOODY_API_URL: "",
    CDN_URL: "https://cdn.fabric.so",
    SOCKETIO_LOG_EVENTS: false,
    isFabricDev: false,
    isFabricProd: false,
    ENVIRONMENT: "",
    TWITTER_EMBED_WIDGET_JS_URL: "",
    APP_STORE_URL: "",
    PLAY_STORE_URL: "",
    PRICING: {
      BASIC_MONTHLY_PLAN_ID: "",
      BASIC_YEARLY_PLAN_ID: "",
      BASIC_OFFERING_ID: "",
      PRO_MONTHLY_PLAN_ID: "",
      PRO_YEARLY_PLAN_ID: "",
      PRO_OFFERING_ID: "",
      MAX_MONTHLY_PLAN_ID: "",
      MAX_YEARLY_PLAN_ID: "",
      MAX_OFFERING_ID: "",
      LIFETIME_BELIEVER_PLAN_ID: ""
    },
    ANALYTICS: {
      MIXPANEL_TOKEN: "",
      DISABLE_PERSISTENCE: false,
      TRACK_PAGEVIEW: true
    },
    MOBILE_DEEPLINK_URL: null
  },
  UI: {
    useOnExpandedResource: () => () => false,
    SHOW_BRAND_MORE: false,
    useEmojiSkinTone: () => ({
      skinTone: SkinTones.NEUTRAL,
      setEmojiPickerSkinTone: noop
    })
  },
  SENTRY: {
    captureException: () => "",
    captureMessage: () => "",
    captureUnderscoreErrorException: () => Promise.resolve(),
    withScope: () => ({}),
    setTag: () => "",
    reactRouterV4BrowserTracingIntegration: () => ({
      name: "",
      setupOnce: () => {
        return;
      }
    })
  },
  OPENREPLAY: {
    zustandPlugin: null
  },
  WAKE_LOCK: {
    lock: async () => {
      if ("wakeLock" in navigator) {
        try {
          if (wakeLockSentinel) {
            await wakeLockSentinel.release();
          }
          wakeLockSentinel = await navigator.wakeLock.request("screen");
        } catch {
        }
      }
    },
    release: async () => {
      if ("wakeLock" in navigator) {
        try {
          if (wakeLockSentinel) {
            await wakeLockSentinel.release();
          }
        } catch {
        }
      }
    },
    isLocked: async () => {
      if (wakeLockSentinel) {
        return !wakeLockSentinel.released;
      }
      return false;
    },
    isSupported: async () => {
      if ("wakeLock" in navigator) {
        return true;
      }
      return false;
    }
  },
  Link: CompatLink,
  Image: CompatImage,
  useRouter: useCompatRouter,
  isInMobile: () => false,
  inNextServer: () => false,
  isInDesktop: () => false,
  useCompatAnyModalOpen: () => false,
  getLocation: () => "web",
  getVersions: () => Promise.resolve({}),
  downloadFile: () => Promise.resolve(),
  getDatabase: () => void 0,
  getPersisterQueriesStoreName: () => "persisted",
  useAuthStatus: () => "unauthenticated",
  useAuthUser: () => null,
  useDefaultResourceClickHandler: () => ({
    defaultClickFolder: noop,
    defaultClickResource: noop,
    defaultClickResourceId: noop
  }),
  setSummarizeText: noop,
  getDefaultRadixPortalContainer: () => void 0,
  getRadixModalModeEnabled: () => true,
  getGlobalEventTarget: () => window,
  getDocumentRoot: () => window.document,
  useOriginUrl: () => {
    return {
      url: void 0
    };
  },
  LANGUAGE: {
    created: "created"
  },
  /**
  * This is setup in the compat layer because with manifest V3 we can't import scripts from external sources.
  */
  TWITTER_EMBED_WIDGET_JS_URL: null,
  ANIMATIONS: {
    ASSISTANT_BUTTON: true
  },
  useResetLocalConnections: () => noop
};
let config = defaultCompatConfig;
const setupCompatConfig = ({ Link: Link2, Image: Image2, ...newConfig }) => {
  const { Link: CLink, Image: CImage, ...rest } = config;
  config = {
    ...merge(rest, newConfig),
    /**
    * we're not deepmerging these two properties as they are react components
    * and we don't really want to deepmerge their properties
    */
    Link: Link2 ?? CLink,
    Image: Image2 ?? CImage
  };
};
const setupCompatLanguageConfig = (newLanguage) => {
  config = {
    ...config,
    LANGUAGE: {
      ...config.LANGUAGE,
      ...omitUndefined(newLanguage)
    }
  };
};
const getCompatConfig = () => {
  return config;
};
const isInMobile = (platform) => config.isInMobile(platform);
const inNextServer = () => {
  return config.inNextServer();
};
const isInDesktop = () => {
  return config.isInDesktop();
};
const useCompatAnyModalOpen = () => {
  return config.useCompatAnyModalOpen();
};
const getLocation = () => {
  return config.getLocation();
};
const getVersions = () => {
  return config.getVersions();
};
const downloadFile = (fileUrl, title, contentType) => {
  return config.downloadFile(fileUrl, title, contentType);
};
const getDatabase = () => {
  return config.getDatabase();
};
const getPersisterQueriesStoreName = () => {
  return config.getPersisterQueriesStoreName();
};
const useAuthStatus = () => {
  return config.useAuthStatus();
};
const useAuthUser = () => {
  return config.useAuthUser();
};
const useDefaultResourceClickHandler = () => {
  return config.useDefaultResourceClickHandler();
};
const useAskAssistantItem = () => {
  return config.useAskAssistantItem?.();
};
const setSummarizeText = (text) => {
  config.setSummarizeText(text);
};
const getDefaultRadixPortalContainer = () => {
  return config.getDefaultRadixPortalContainer();
};
const getRadixModalModeEnabled = () => {
  return config.getRadixModalModeEnabled();
};
const useRouter = () => {
  return config.useRouter();
};
const getGlobalEventTarget = () => {
  return config.getGlobalEventTarget();
};
const getDocumentRoot = () => {
  return config.getDocumentRoot();
};
const useOriginUrl = () => {
  return config.useOriginUrl();
};
const Link = forwardRef((props, ref) => {
  return React.createElement(config.Link, {
    ...props,
    ref
  });
});
Link.displayName = "CompatLink";
const Image = forwardRef((props, ref) => {
  return React.createElement(config.Image, {
    ...props,
    ref
  });
});
Image.displayName = "CompatImage";
const getCompatLanguage = () => {
  return config.LANGUAGE;
};
const getCompatAnimations = () => {
  return config.ANIMATIONS;
};
const setCompatAnimations = (newAnimations) => {
  setupCompatConfig({
    ANIMATIONS: {
      ...config.ANIMATIONS,
      ...newAnimations
    }
  });
};
const useResetLocalConnections = () => {
  return config.useResetLocalConnections();
};

export { Image, Link, defaultCompatConfig, downloadFile, getCompatAnimations, getCompatConfig, getCompatLanguage, getDatabase, getDefaultRadixPortalContainer, getDocumentRoot, getGlobalEventTarget, getLocation, getPersisterQueriesStoreName, getRadixModalModeEnabled, getVersions, inNextServer, isInDesktop, isInMobile, setCompatAnimations, setSummarizeText, setupCompatConfig, setupCompatLanguageConfig, useAskAssistantItem, useAuthStatus, useAuthUser, useCompatAnyModalOpen, useDefaultResourceClickHandler, useOriginUrl, useResetLocalConnections, useRouter };
//# sourceMappingURL=compat.js.map
